import { makeAutoObservable, runInAction } from 'mobx';
import { DEFAULT_PAGE_SIZE, AGGR_CHAT_USER } from 'src/constants/constants';
import { serviceStore } from '../ServiceStore';
import { logger } from 'src/utils/logger';

/**
 * 统一查询服务
 * 合并了 DataInitializationService 和 SearchParamsStore 的功能
 * 专门负责管理 /api/erp/v1/view/pageList 的查询条件
 */
export class UnifiedQueryService {
  // 基础状态
  _isInitializing = false;
  _initializationComplete = false;
  _hasInitializedView = false;

  // 查询参数状态
  searchValue = '';
  currentPage = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  currentViewId = '';
  sortOrder = 'desc';

  // 查询条件
  queryConditions = [];

  // 依赖注入
  _erpDatabaseStore = null;
  _viewManagementService = null;

  constructor(erpDatabaseStore, viewManagementService) {
    this._erpDatabaseStore = erpDatabaseStore;
    this._viewManagementService = viewManagementService;

    makeAutoObservable(this, {
      _buildQueryConditions: false,
      _validateSearchValue: false,
      _erpDatabaseStore: false,
      _viewManagementService: false,
    });
  }

  // 计算属性
  get isInitializing() {
    return this._isInitializing;
  }

  get initializationComplete() {
    return this._initializationComplete;
  }

  get hasInitializedView() {
    return this._hasInitializedView;
  }

  get hasSearchConditions() {
    return this.searchValue && this.searchValue.trim().length > 0;
  }

  get currentSearchParams() {
    if (!this.currentViewId) {
      logger.warn('[UnifiedQueryService] currentViewId 为空，无法构建查询参数');
      return null;
    }

    return {
      page: {
        current: this.currentPage,
        pageSize: this.pageSize,
      },
      viewId: this.currentViewId,
      query: this.queryConditions,
      sort: {
        last_receive_time: this.sortOrder,
      },
    };
  }

  get timerUpdateParams() {
    if (!this.currentViewId) {
      logger.warn('[UnifiedQueryService] currentViewId 为空，无法构建定时更新参数');
      return null;
    }

    return {
      page: {
        current: 1,
        pageSize: this.pageSize,
      },
      viewId: this.currentViewId,
      query: this.queryConditions,
      sort: {
        last_receive_time: this.sortOrder,
      },
    };
  }

  get paginationParams() {
    if (!this.currentViewId) {
      logger.warn('[UnifiedQueryService] currentViewId 为空，无法构建分页参数');
      return null;
    }

    return {
      page: {
        current: this.currentPage,
        pageSize: this.pageSize,
      },
      viewId: this.currentViewId,
      query: this.queryConditions,
      sort: {
        last_receive_time: this.sortOrder,
      },
    };
  }

  // 初始化方法
  async initializeData(setTableData, setError) {
    if (this._initializationComplete || this._isInitializing) {
      return;
    }

    try {
      this._setInitializing(true);

      // 先尝试获取表列表，如果失败则记录日志但不中断流程
      try {
        await this._erpDatabaseStore.fetchTableList();
        logger.debug('[UnifiedQueryService] 表列表获取成功');
      } catch (tableListError) {
        logger.warn('[UnifiedQueryService] 表列表获取失败，继续尝试获取表详情:', tableListError);
      }

      // 尝试获取表详情（使用 ErpDatabaseStore 的缓存机制）
      let tableData = null;
      try {
        tableData = await this._erpDatabaseStore.fetchTableDetail(AGGR_CHAT_USER);
        logger.debug('[UnifiedQueryService] 表详情获取成功');
      } catch (tableDetailError) {
        logger.error('[UnifiedQueryService] 表详情获取失败:', tableDetailError);
        // 设置错误信息但不中断流程
        setError(`获取表详情失败: ${tableDetailError.message}`);
        // 尝试使用默认的空数据结构
        tableData = {
          id: AGGR_CHAT_USER,
          name: '聚合聊天用户',
          views: [],
          columns: [],
        };
      }

      // 设置表数据（即使是默认的空数据）
      setTableData(tableData);

      // 如果有视图数据，尝试初始化视图
      if (tableData?.views?.length > 0) {
        try {
          await this._initializeView(tableData);
        } catch (viewError) {
          logger.warn('[UnifiedQueryService] 视图初始化失败:', viewError);
        }
      } else {
        // 修复：即使没有视图数据，也要设置空的viewData，确保VirtualScrollUserStore能完成初始化
        if (serviceStore && typeof serviceStore.setViewData === 'function') {
          serviceStore.setViewData({
            data: [],
            total: 0,
          });
        }
      }

      this._setInitializationComplete(true);
      logger.debug('[UnifiedQueryService] 初始化数据完成');
    } catch (error) {
      logger.error('[UnifiedQueryService] 初始化数据失败:', error);
      setError(error.message || '初始化数据失败');
    } finally {
      this._setInitializing(false);
    }
  }

  async _initializeView(tableData) {
    // 修复：如果没有视图数据，直接返回，不发起API请求
    if (!tableData?.views || tableData.views.length === 0) {
      return;
    }

    const needsViewInitialization =
      !this._viewManagementService.selectedViewId || !this._hasInitializedView;

    if (needsViewInitialization) {
      this._setHasInitializedView(true);

      const lastSelectedViewId = this._viewManagementService.restoreLastSelectedView();
      let targetViewId = null;

      logger.info('[UnifiedQueryService] ===== 视图初始化 =====');
      logger.info('[UnifiedQueryService] 恢复的视图ID:', { lastSelectedViewId });
      logger.info('[UnifiedQueryService] 可用视图:', {
        viewCount: tableData.views.length,
        viewIds: tableData.views.map(v => v.id),
      });

      if (
        lastSelectedViewId &&
        this._viewManagementService.isViewExists(lastSelectedViewId, tableData.views)
      ) {
        targetViewId = lastSelectedViewId;
        logger.info('[UnifiedQueryService] 使用恢复的视图:', { targetViewId });
      } else {
        targetViewId = tableData.views[0].id;
        logger.info('[UnifiedQueryService] 使用默认视图:', { targetViewId });
      }

      this._viewManagementService.setSelectedViewId(targetViewId);
      this.setCurrentViewId(targetViewId);

      // 新版本：视图初始化完成后获取第一页数据，确保 ServiceStore 有初始数据
      // 这样 VirtualScrollUserStore 就能正常初始化，避免死锁
      try {
        logger.info('[UnifiedQueryService] 视图初始化完成，开始获取第一页数据');

        // 获取第一页数据
        const initialRequest = {
          page: { current: 1, pageSize: 20 },
          viewId: targetViewId,
          sort: { last_receive_time: 'desc' },
        };

        // 直接使用静态引用的 serviceStore
        logger.info('[UnifiedQueryService] 调用 serviceStore.fetchViewData');
        await serviceStore.fetchViewData(initialRequest, { showLoading: false });
        logger.info('[UnifiedQueryService] 第一页数据获取完成');
      } catch (error) {
        logger.error('[UnifiedQueryService] 获取第一页数据失败:', error);
        // 即使获取失败，也不影响初始化完成状态
      }
      logger.info('[UnifiedQueryService] ===== 视图初始化完成 =====');
    }
  }

  // 动作方法
  _setInitializing(value) {
    this._isInitializing = value;
  }

  _setInitializationComplete(value) {
    this._initializationComplete = value;
  }

  _setHasInitializedView(value) {
    this._hasInitializedView = value;
  }

  setSearchValue = value => {
    logger.debug('[UnifiedQueryService] 设置搜索值:', value);
    this.searchValue = value || '';
    this._updateQueryConditions();
  };

  setCurrentPage = page => {
    this.currentPage = page;
  };

  setPageSize = size => {
    this.pageSize = size;
  };

  setSortOrder = order => {
    this.sortOrder = order;
  };

  setCurrentViewId = viewId => {
    const oldViewId = this.currentViewId;

    logger.debug('[UnifiedQueryService] 设置视图ID:', {
      oldViewId: oldViewId,
      newViewId: viewId,
      searchValue: this.searchValue,
      queryConditionsLength: this.queryConditions.length,
    });

    this.currentViewId = viewId;

    // 视图切换后，确保查询条件是最新的
    if (viewId !== oldViewId) {
      this.ensureQueryConditions();
    }
  };

  /**
   * 确保查询条件是最新的
   * 在虚拟滚动等场景下，确保查询条件不会丢失
   */
  ensureQueryConditions = () => {
    logger.debug('[UnifiedQueryService] ensureQueryConditions 被调用', {
      searchValue: this.searchValue,
      searchValueLength: this.searchValue?.length,
      queryConditionsLength: this.queryConditions.length,
      hasSearchConditions: this.hasSearchConditions,
    });

    // 如果 searchValue 存在但 queryConditions 为空，则重新构建查询条件
    if (
      this.searchValue &&
      this.searchValue.trim().length > 0 &&
      this.queryConditions.length === 0
    ) {
      logger.debug('[UnifiedQueryService] 检测到查询条件丢失，重新构建:', this.searchValue);
      this._updateQueryConditions();
      logger.debug('[UnifiedQueryService] 查询条件重新构建完成:', this.queryConditions);
    } else if (!this.searchValue || this.searchValue.trim().length === 0) {
      logger.warn('[UnifiedQueryService] searchValue 为空，无法恢复查询条件');
    } else if (this.queryConditions.length > 0) {
      logger.debug('[UnifiedQueryService] 查询条件已存在，无需重新构建');
    }
  };

  resetQueryState = () => {
    logger.debug('[UnifiedQueryService] resetQueryState 被调用，清空搜索条件');
    runInAction(() => {
      this.searchValue = ''; // 清空搜索值
      this.currentPage = 1;
      this.queryConditions = [];
    });
  };

  resetPaginationState = () => {
    runInAction(() => {
      this.currentPage = 1;
    });
  };

  resetAllState = () => {
    logger.warn('[UnifiedQueryService] resetAllState 被调用，这可能会清空所有状态');
    runInAction(() => {
      // this.searchValue = '';
      this.currentPage = 1;
      this.pageSize = DEFAULT_PAGE_SIZE;
      this.queryConditions = [];
      this.sortOrder = 'desc';
      this.currentViewId = '';
      this._isInitializing = false;
      this._initializationComplete = false;
      this._hasInitializedView = false;
    });
  };

  // 私有方法
  _updateQueryConditions = () => {
    if (!this.hasSearchConditions) {
      this.queryConditions = [];
      return;
    }

    const searchTerm = this.searchValue.trim();

    this.queryConditions = [
      {
        columnId: 'user_name',
        op: 'like',
        value: searchTerm,
      },
    ];
  };

  // 工具方法
  getSearchParamsCopy = (type = 'current') => {
    switch (type) {
      case 'timer':
        return JSON.parse(JSON.stringify(this.timerUpdateParams));
      case 'pagination':
        return JSON.parse(JSON.stringify(this.paginationParams));
      case 'current':
      default:
        return JSON.parse(JSON.stringify(this.currentSearchParams));
    }
  };

  isValidSearchParams = () => {
    return this.currentViewId && this.pageSize > 0;
  };

  getSearchSummary = () => {
    return {
      hasSearch: this.hasSearchConditions,
      searchValue: this.searchValue,
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalConditions: this.queryConditions.length,
      viewId: this.currentViewId,
      sortOrder: this.sortOrder,
      isInitializing: this._isInitializing,
      initializationComplete: this._initializationComplete,
      hasInitializedView: this._hasInitializedView,
    };
  };

  destroy = () => {
    this.resetAllState();
  };
}
